import { useFormik } from "formik";
import { IProfileFormFields } from "./ProfileForm.interfaces";
import * as yup from 'yup';
import { useToast } from "@netfront/ekardo-content-library";
import { useGetLoggedUser, useUpdateUser } from "@netfront/gelada-identity-library";
import { useEffect, useRef, useState } from "react";
import { Button, Input, SkeletonLoader, ToggleSwitch } from "@netfront/ui-library";
import { gql } from '@apollo/client';
import { EUserNotificationPreference, useGetHowlerUserSettingsLazyQuery, useUpdateHowlerUserSettingsMutation } from "@/services/howler/generated.graphql";
import { howlerClient } from "@/services";
import { useGroups } from "@/hooks";
import { shouldIncludePhone } from "@/utils";

export const ProfileForm = () => {
  const { hasSMS, userType } = useGroups();
  const { handleToastSuccess, handleToastError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldReceiveSms, setShouldReceiveSms] = useState<boolean>(true);
  const originalEmailRef = useRef<string>('');
  const emailChangedRef = useRef<boolean>(false);

  const hasPhoneFields = shouldIncludePhone(userType?.id);

  const { handleUpdateUser, isLoading: isFormSubmitting } = useUpdateUser({
    onCompleted: () => {
      if (emailChangedRef.current) {
        handleToastSuccess({
          message: 'A verification email has been sent to your new email address. Please check your inbox and enter the code to confirm the change.',
          durationInMilliseconds: 8000
        });
      } else {
        handleToastSuccess({
          message: 'Profile updated successfully',
          durationInMilliseconds: 6000
        });
      }
    },
    mutation: gql`
      mutation updateUser($email: String!, $firstName: String!, $lastName: String!) {
        user {
          updateEmail(email: $email)
          updateUser(firstname: $firstName, lastname: $lastName)
        }
      }
    `
  });

  const formik = useFormik<IProfileFormFields>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
    validationSchema: yup.object().shape({
      firstName: yup.string().required('Please enter in a first name'),
      lastName: yup.string().required('Please enter in a last name'),
      email: yup.string().email().required('Please enter in an email address'),
      phoneNumber: yup
        .string()
        .transform((value) => value?.replace(/\s+/g, ''))
        .matches(
          /^(\+61[2-478]\d{8}|0[2-478]\d{8})$/,
          'Please enter a valid Australian phone number'
        )
    }),
    onSubmit: async ({ email, firstName, lastName }: IProfileFormFields) => {
      emailChangedRef.current = email !== originalEmailRef.current;
      handleUpdateUser({
        email,
        firstName,
        lastName,
      });
    }
  });

  const [getUserSettings] = useGetHowlerUserSettingsLazyQuery({
    client: howlerClient,
    onCompleted: ({ user }) => {
      setShouldReceiveSms(Boolean(user?.getUser?.settings?.includes(EUserNotificationPreference.Sms)))
    }
  });

  const [updateUserSettings] = useUpdateHowlerUserSettingsMutation({
    client: howlerClient,
    onCompleted: ({ user }) => {
      setShouldReceiveSms(Boolean(user?.updateSettings?.settings?.includes(EUserNotificationPreference.Sms)))
    }
  });

  const { handleGetLoggedUser } = useGetLoggedUser({
    onCompleted: ({ user }) => {
      const email = user.credential.email ?? '';
      originalEmailRef.current = email;
      formik.setValues({
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email,
        phoneNumber: user.credential.phoneNumber ?? '',
      });
      setIsLoading(false);
      getUserSettings();
    },
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true
      })
    }
  });

  useEffect(() => {
    handleGetLoggedUser();
  }, []);

  const { errors, touched, handleBlur, handleChange, handleSubmit, values } = formik;

  if (isLoading) return (
    <>
      <SkeletonLoader height='6rem' width='100%' />
    </>
  );

  return(
    <form onSubmit={handleSubmit}>
      <div className="flex flex-wrap -mx-4">
        <div className="w-full md:w-1/2 px-4">
          <Input
            additionalClassNames="!m-0"
            errorMessage={touched.firstName && errors.firstName ? errors.firstName : ''}
            id='firstName'
            labelText='First name'
            placeholder='Enter first name'
            name='firstName'
            onChange={handleChange}
            type='text'
            value={values.firstName}
            onBlur={handleBlur}
            isRequired
          />
        </div>

        <div className="w-full md:w-1/2 px-4">
          <Input
            errorMessage={touched.lastName && errors.lastName ? errors.lastName : ''}
            id='lastName'
            labelText='Last name'
            placeholder='Enter last name'
            name='lastName'
            onChange={handleChange}
            type='text'
            value={values.lastName}
            onBlur={handleBlur}
            isRequired
          />
        </div>

        <div className="w-full md:w-1/2 px-4">
          <Input
            errorMessage={touched.email && errors.email ? errors.email : ''}
            id='email'
            labelText='Email'
            placeholder='Enter email'
            name='email'
            onChange={handleChange}
            type='email'
            value={values.email}
            onBlur={handleBlur}
          />
        </div>

        {hasPhoneFields && (
          <div className="w-full md:w-1/2 px-4 flex flex-col">
            <Input
              isReadOnly
              errorMessage={touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : ''}
              id='phoneNumber'
              labelText='Phone number'
              name='phoneNumber'
              placeholder='Enter your phone number'
              type='text'
              value={values.phoneNumber}
              onChange={handleChange}
              onBlur={handleBlur}
            /> 
          </div>
        )}

        {hasSMS && (
          <div className="w-full md:w-1/2 px-4">
            <ToggleSwitch
              id={EUserNotificationPreference.Sms}
              isChecked={shouldReceiveSms}
              labelText="Receive SMS notifcations"
              onChange={({ currentTarget: { checked } }) => {
                updateUserSettings({
                  variables: {
                    inputType: [
                      EUserNotificationPreference.Email,
                      EUserNotificationPreference.PushNotification,
                      ...(checked ? [EUserNotificationPreference.Sms] : [])
                    ]
                  }
                })
              }}
            />
          </div>
        )}

      </div>

      <div className="flex mt-6">
        {hasPhoneFields && (
          <Button
            additionalClassNames="my-auto"
            linkButton={{ id: 'verify', url: '/user/verify-phone' }}
            text="Change your phone number"
          />
        )}

        <Button
          additionalClassNames="ml-auto"
          text='Update'
          type='submit'
          isLoading={isFormSubmitting}
        />
      </div>

    </form>

  )
};